import { ImportRequestSource } from 'services/import-requests/enums'

import {
  RequestActivityRequestType,
  ReviewActivityActorType,
  ReviewActivityEventType,
  ReviewActivitySharingOutcome
} from './enums'
import {
  type RequestActivityEvent,
  RequestActivityEventType,
  type RequestActivitySource,
  type ReviewActivityEvent
} from './types'

const joinNotes = (...parts: Array<string | undefined>): string | undefined => {
  const present = parts.filter((part): part is string => Boolean(part?.trim()))
  return present.length > 0 ? present.join(' · ') : undefined
}

const getTrimmed = (value?: string | null): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const getActorEmail = (event: ReviewActivityEvent): string | undefined =>
  getTrimmed(event.actor.email) ?? getTrimmed(event.actor.displayName)

const getUserEmail = (user?: ReviewActivityEvent['newAssignee']): string | undefined => {
  if (!user) return undefined
  return getTrimmed(user.email) ?? getTrimmed(user.displayName)
}

const getSnapshotId = (user?: { _id?: string; id?: string } | null): string | undefined => user?._id ?? user?.id

const getActorName = (event: ReviewActivityEvent): string | undefined =>
  getTrimmed(event.actor.displayName) ?? getTrimmed(event.actor.email)

const getUserName = (user?: ReviewActivityEvent['newAssignee']): string | undefined => {
  if (!user) return undefined
  return getTrimmed(user.displayName) ?? getTrimmed(user.email)
}

const isSelfAssignment = (event: ReviewActivityEvent): boolean => {
  const actorId = getSnapshotId(event.actor)
  const assigneeId = getSnapshotId(event.newAssignee)

  return Boolean(actorId && assigneeId && actorId === assigneeId)
}

const isSystemActor = (event: ReviewActivityEvent): boolean => event.actor.type === ReviewActivityActorType.System

const isImportRequest = (source: RequestActivitySource): boolean =>
  source.requestType === RequestActivityRequestType.Import

const isExternalImport = (source: RequestActivitySource): boolean =>
  isImportRequest(source) && source.importSource === ImportRequestSource.ExternalS3Location

const getLabelName = (event: ReviewActivityEvent): string => getTrimmed(event.label?.name) ?? '[deleted label]'

const mapSubmitted = (event: ReviewActivityEvent, source: RequestActivitySource): RequestActivityEvent => {
  const sourceNote = isImportRequest(source)
    ? isExternalImport(source)
      ? 'external S3 source'
      : 'uploaded file'
    : undefined

  return {
    id: event._id,
    type: RequestActivityEventType.Submitted,
    title: 'Request submitted',
    note: joinNotes(getActorEmail(event), getTrimmed(event.workspace?.name), sourceNote),
    at: event.occurredAt
  }
}

const mapAssigneeAdded = (event: ReviewActivityEvent): RequestActivityEvent => {
  if (isSystemActor(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Assigned,
      title: 'Assigned by policy',
      note: getUserEmail(event.newAssignee) && `Escalated to ${getUserEmail(event.newAssignee)}`,
      at: event.occurredAt
    }
  }

  const actorName = getActorName(event)
  const assigneeName = getUserName(event.newAssignee)

  if (isSelfAssignment(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Assigned,
      title: actorName ? `Assigned by ${actorName} (self)` : 'Assigned (self)',
      at: event.occurredAt
    }
  }

  if (event.reviewConfirmed === true) {
    return {
      id: event._id,
      type: RequestActivityEventType.PeerReviewed,
      title: actorName ? `Reviewed and referred by ${actorName}` : 'Reviewed and referred',
      note: assigneeName && `Referred to ${assigneeName}`,
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.Assigned,
    title: actorName ? `Assigned by ${actorName}` : 'Assigned',
    note: assigneeName && `Assigned to ${assigneeName}`,
    at: event.occurredAt
  }
}

const mapAssigneeChanged = (event: ReviewActivityEvent): RequestActivityEvent => {
  if (isSystemActor(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Assigned,
      title: 'Assigned by policy',
      note:
        getUserEmail(event.previousAssignee) && getUserEmail(event.newAssignee)
          ? `Escalated from ${getUserEmail(event.previousAssignee)} to ${getUserEmail(event.newAssignee)}`
          : undefined,
      at: event.occurredAt
    }
  }

  const actorName = getActorName(event)
  const previousName = getUserName(event.previousAssignee)
  const assigneeName = getUserName(event.newAssignee)
  const fromToNote = previousName && assigneeName ? `From ${previousName} to ${assigneeName}` : undefined

  if (isSelfAssignment(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Assigned,
      title: actorName ? `Assigned by ${actorName} (self)` : 'Assigned (self)',
      note: fromToNote,
      at: event.occurredAt
    }
  }

  if (event.reviewConfirmed === true) {
    return {
      id: event._id,
      type: RequestActivityEventType.PeerReviewed,
      title: actorName ? `Reviewed and referred by ${actorName}` : 'Reviewed and referred',
      note: fromToNote,
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.Assigned,
    title: actorName ? `Assigned by ${actorName}` : 'Assigned',
    note: fromToNote,
    at: event.occurredAt
  }
}

const mapAssigneeRemoved = (event: ReviewActivityEvent): RequestActivityEvent => {
  if (isSystemActor(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Unassigned,
      title: 'Returned to manual review',
      note: getUserEmail(event.previousAssignee) && `${getUserEmail(event.previousAssignee)} was unassigned by policy`,
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.Unassigned,
    title: 'Unassigned',
    note:
      getActorEmail(event) && getUserEmail(event.previousAssignee)
        ? `${getActorEmail(event)} removed ${getUserEmail(event.previousAssignee)}`
        : undefined,
    at: event.occurredAt
  }
}

const mapApproved = (event: ReviewActivityEvent, source: RequestActivitySource): RequestActivityEvent => {
  if (!isSystemActor(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Approved,
      title: 'Request approved',
      note: getActorEmail(event) && `Approved by ${getActorEmail(event)}`,
      at: event.occurredAt
    }
  }

  if (isExternalImport(source) && source.importCounts) {
    return {
      id: event._id,
      type: RequestActivityEventType.Approved,
      title: 'Request automatically approved',
      note: `${source.importCounts.clean} of ${source.importCounts.total} objects clean`,
      at: event.occurredAt
    }
  }

  if (isImportRequest(source)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Approved,
      title: 'Request automatically approved',
      note: 'Clean scan',
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.Approved,
    title: 'Request automatically approved',
    note: 'Approved by policy',
    at: event.occurredAt
  }
}

const mapRejected = (event: ReviewActivityEvent, source: RequestActivitySource): RequestActivityEvent => {
  if (!isSystemActor(event)) {
    return {
      id: event._id,
      type: RequestActivityEventType.Rejected,
      title: 'Request rejected',
      note: joinNotes(getActorEmail(event) && `Rejected by ${getActorEmail(event)}`, source.rejectionReason),
      at: event.occurredAt
    }
  }

  if (isExternalImport(source) && source.importCounts) {
    const { threatDetected, unsupported, total } = source.importCounts
    const note =
      threatDetected > 0
        ? `Malware threat detected in ${threatDetected} of ${total} objects`
        : `No releasable objects · ${unsupported} of ${total} unsupported`

    return {
      id: event._id,
      type: RequestActivityEventType.Rejected,
      title: 'Request automatically rejected',
      note,
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.Rejected,
    title: 'Request automatically rejected',
    note: source.rejectionReason,
    at: event.occurredAt
  }
}

const mapSharing = (event: ReviewActivityEvent): RequestActivityEvent => {
  const workspaceName = getTrimmed(event.sharing?.workspace.name)

  if (event.sharing?.outcome === ReviewActivitySharingOutcome.Rejected) {
    return {
      id: event._id,
      type: RequestActivityEventType.DataAccessShared,
      title: 'Sharing rejected',
      note: workspaceName && `Sharing with ${workspaceName} was rejected`,
      at: event.occurredAt
    }
  }

  return {
    id: event._id,
    type: RequestActivityEventType.DataAccessShared,
    title: 'Dataset shared',
    note: workspaceName && `Shared with ${workspaceName}`,
    at: event.occurredAt
  }
}

export const mapRequestActivityEvent = (
  event: ReviewActivityEvent,
  source: RequestActivitySource
): RequestActivityEvent => {
  switch (event.eventType) {
    case ReviewActivityEventType.RequestSubmitted:
      return mapSubmitted(event, source)
    case ReviewActivityEventType.AssigneeAdded:
      return mapAssigneeAdded(event)
    case ReviewActivityEventType.AssigneeChanged:
      return mapAssigneeChanged(event)
    case ReviewActivityEventType.AssigneeRemoved:
      return mapAssigneeRemoved(event)
    case ReviewActivityEventType.LabelAdded:
      return {
        id: event._id,
        type: RequestActivityEventType.LabelAdded,
        title: 'Label added',
        note: joinNotes(getLabelName(event), getActorEmail(event) && `by ${getActorEmail(event)}`),
        at: event.occurredAt
      }
    case ReviewActivityEventType.LabelRemoved:
      return {
        id: event._id,
        type: RequestActivityEventType.LabelRemoved,
        title: 'Label removed',
        note: joinNotes(getLabelName(event), getActorEmail(event) && `by ${getActorEmail(event)}`),
        at: event.occurredAt
      }
    case ReviewActivityEventType.RequestApproved:
      return mapApproved(event, source)
    case ReviewActivityEventType.RequestRejected:
      return mapRejected(event, source)
    case ReviewActivityEventType.AppealSubmitted:
      return {
        id: event._id,
        type: RequestActivityEventType.AppealSubmitted,
        title: 'Appeal submitted',
        note: joinNotes(getActorEmail(event), getTrimmed(event.workspace?.name), 'Appeal form attached'),
        at: event.occurredAt
      }
    case ReviewActivityEventType.AttestationRequested:
      return {
        id: event._id,
        type: RequestActivityEventType.AttestationRequested,
        title: 'Additional information requested',
        note: isSystemActor(event)
          ? 'Requested by policy'
          : getActorEmail(event) && `Requested by ${getActorEmail(event)}`,
        at: event.occurredAt
      }
    case ReviewActivityEventType.AttestationSubmitted:
      return {
        id: event._id,
        type: RequestActivityEventType.AttestationSubmitted,
        title: 'Additional information submitted',
        note: joinNotes(getActorEmail(event), getTrimmed(event.workspace?.name), 'Additional information submitted'),
        at: event.occurredAt
      }
    case ReviewActivityEventType.DataAccessShared:
      return mapSharing(event)
    default:
      return {
        id: event._id,
        type: RequestActivityEventType.Submitted,
        title: 'Activity',
        note: getActorEmail(event),
        at: event.occurredAt
      }
  }
}
